import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated, logout } from '../utils/auth';
import Podium from './Podium';
import Race from './Race';
import PageHeader from './PageHeader';
import NotificationBell from './NotificationBell';
import './Dashboard.css';

const fetchOptions = { credentials: 'include', headers: { 'Content-Type': 'application/json' } };

const Dashboard = () => {
  const [races, setRaces] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [userPicks, setUserPicks] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedRace, setSelectedRace] = useState(null);
  const [selectedYear, setSelectedYear] = useState(undefined);
  const [availableYears, setAvailableYears] = useState([]);
  const navigate = useNavigate();

  const handleUnauthorized = useCallback(() => {
    fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
    logout();
    navigate('/login');
  }, [navigate]);

  const fetchRaces = useCallback(async (year) => {
    try {
      const url = year ? `/api/races?year=${year}` : '/api/races';
      const response = await fetch(url, fetchOptions);
      if (response.status === 401) { handleUnauthorized(); return; }
      const data = await response.json();
      setRaces(data);
    } catch (error) {
      console.error('Error fetching races:', error);
    } finally {
      setLoading(false);
    }
  }, [handleUnauthorized]);

  const fetchDrivers = useCallback(async () => {
    try {
      const response = await fetch('/api/drivers', fetchOptions);
      if (response.status === 401) { handleUnauthorized(); return; }
      const data = await response.json();
      setDrivers(data);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  }, [handleUnauthorized]);

  const fetchUserPicks = useCallback(async () => {
    try {
      const response = await fetch('/api/picks', fetchOptions);
      if (response.status === 401) { handleUnauthorized(); return; }
      const data = await response.json();
      const picksMap = {};
      data.forEach(pick => {
        const raceKey = pick.raceId?._id || pick.raceId;
        picksMap[raceKey] = pick;
      });
      setUserPicks(picksMap);
    } catch (error) {
      console.error('Error fetching picks:', error);
    }
  }, [handleUnauthorized]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    const init = async () => {
      try {
        const yearsRes = await fetch('/api/races/years', fetchOptions);
        if (yearsRes.status === 401) { handleUnauthorized(); return; }
        const years = await yearsRes.json();
        setAvailableYears(years);
        setSelectedYear(years.length > 0 ? years[0] : null);
      } catch (error) {
        console.error('Error fetching years:', error);
        setSelectedYear(null);
      }
    };

    init();
    fetchDrivers();
    fetchUserPicks();
  }, [navigate, fetchDrivers, fetchUserPicks, handleUnauthorized]);

  useEffect(() => {
    if (selectedYear !== undefined) {
      fetchRaces(selectedYear);
    }
  }, [selectedYear, fetchRaces]);

  const handleSubmitPick = async (raceId, picks) => {
    try {
      const isUpdate = !!userPicks[raceId];
      const response = await fetch('/api/picks', {
        method: 'POST',
        ...fetchOptions,
        body: JSON.stringify({ raceId, ...picks }),
      });
      if (response.status === 401) { handleUnauthorized(); return; }
      
      const data = await response.json();
      
      if (!response.ok) {
        console.error('Error submitting pick:', data);
        alert(`Error saving pick: ${data.error || 'Unknown error'}`);
        return;
      }
      
      setUserPicks(prev => ({ ...prev, [raceId]: data }));
      alert(isUpdate ? 'Pick updated successfully!' : 'Pick saved successfully!');
      setSelectedRace(null);
    } catch (error) {
      console.error('Error submitting pick:', error);
      alert('Failed to save pick. Please try again.');
    }
  };

  const handleLogout = async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
    logout();
    navigate('/login');
  };

  if (loading) {
    return <div className="dashboard"><p>Loading races...</p></div>;
  }

  if (selectedRace) {
    return (
      <div className="dashboard">
        <button onClick={() => setSelectedRace(null)} className="pick-button" style={{ maxWidth: 200, marginBottom: '1rem' }}>
          ← Back to Dashboard
        </button>
        <Podium
          raceId={selectedRace._id}
          drivers={drivers}
          onSubmit={(picks) => handleSubmitPick(selectedRace._id, picks)}
          existingPick={userPicks[selectedRace._id]}
        />
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <PageHeader overline="Formula 1 Prediction Game" title="PODIUM PICKER" />
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <NotificationBell />
          <button onClick={() => navigate('/history')} className="pick-button" style={{ maxWidth: 140 }}>
            My History
          </button>
          <button onClick={() => navigate('/profile')} className="pick-button" style={{ maxWidth: 140 }}>
            My Profile
          </button>
          <button onClick={() => navigate('/leaderboard')} className="pick-button" style={{ maxWidth: 140 }}>
            Standings
          </button>
          <button onClick={() => navigate('/groups')} className="pick-button" style={{ maxWidth: 140 }}>
            Groups
          </button>
          <button onClick={handleLogout} className="pick-button" style={{ maxWidth: 120 }}>
            Logout
          </button>
        </div>
      </div>

      {availableYears.length > 0 && (
        <div className="season-selector">
          <span className="season-label">Season:</span>
          {availableYears.map(year => (
            <button
              key={year}
              className={`season-btn${selectedYear === year ? ' season-btn--active' : ''}`}
              onClick={() => setSelectedYear(year)}
            >
              {year}
            </button>
          ))}
        </div>
      )}

      {selectedYear && <h2 className="season-heading">{selectedYear} Season</h2>}

      {races.length === 0 ? (
        <p style={{ textAlign: 'center' }}>No upcoming races found.</p>
      ) : (
        <div className="races-grid">
          {races.map(race => (
            <Race
              key={race._id}
              race={race}
              hasPick={!!userPicks[race._id]}
              onPickClick={() => setSelectedRace(race)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Dashboard;